import { useState } from 'owa-react';
import { FluentMenu } from 'owa-fluent-v9-shims';
import calculateColumnWidths from '../utils/calculateColumnWidths';
import { observer } from 'owa-mobx-react';
import { type FolderForestNodeType } from 'owa-favorites-types';
import loc, { format } from 'owa-localize';
import { lazySelectSort } from 'owa-mail-filter-actions';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import { getAnchorForContextMenu } from 'owa-positioning';
import { useMessageListTextStyle } from 'owa-mail-listitem-styles/lib/utils/useMessageListTextStyle';
import { useResizeObserver } from 'owa-react-hooks/lib/useResizeObserver';
import {
    ResizeHandle,
    ResizeHandleScenario,
    lazyUpdateScenarioInResizeMode,
} from 'owa-resize-handle';
import type SortDirection from 'owa-service/lib/contract/SortDirection';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import React from 'react';
import {
    getSortByForTable,
    getUserCategoryName,
    getViewFilterForTable,
    isDumpsterTable,
    getStore as getListViewStore,
    type SortColumn,
    type TableQueryType,
    getIsSearchTableShown,
    isCopilotResultsTable,
    isPriorityView,
    type MailFolderTableQuery,
} from 'owa-mail-list-store';
import {
    columnHeaderAriaLabel,
    fromColumnLabel,
    modifiedColumnLabel,
    receivedColumnLabel,
    returnTimeColumnLabel,
    sentColumnLabel,
    sortAscendingLabel,
    sortDescendingLabel,
    subjectColumnLabel,
    toColumnLabel,
    resizeButtonLabel,
} from './MailListColumnHeaders.locstring.json';
import type { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { DirectionalHint } from '@fluentui/react/lib/ContextualMenu';
import {
    getStore as getMailListLayoutStore,
    onColumnHeaderWidthsUpdated,
    onFirstColumnHandleChanged,
    onSecondColumnHandleChanged,
    type MailListColumnHeaderType,
    getColumnMinWidth,
} from 'owa-mail-layout';
import { getDisplayDensityMenuItem } from 'owa-display-density-option';
import type { IPoint } from '@fluentui/react/lib/Utilities';
import { wrapForcedLayout } from 'owa-performance';
import {
    columnHeadersContainer,
    emptyFirstColumn,
    columnHeader,
    resizeHandle,
    columnHeaderHovered,
    columnHeaderRoomy,
    columnHeaderCozy,
    columnHeaderCompact,
    sortIconRoomy,
    sortIconCompactOrCozy,
    columnHeadersContainerDenseless,
    bleedthroughColumnHeader,
    columnHeaderContainer,
    senderColumnButtonMargin,
    resizeHandleDeprecated,
    rpDenseless,
    newFocusStyles,
    sortingEnabled,
    receivedColumnContainer,
    columnHeadersContainerFlexBox,
    mlColumnHeaderHover,
} from './MailListColumnHeaders.scss';
import classnames from 'owa-classnames';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ChevronDownRegular, ChevronUpRegular } from '@fluentui/react-icons';

export interface MailListColumnHeadersProps {
    tableViewId: string;
    folderId: string;
}
const MailListColumnHeaders: React.FunctionComponent<MailListColumnHeadersProps> = observer(
    function MailListColumnHeaders(props: MailListColumnHeadersProps) {
        const { folderId, tableViewId } = props;
        const densityModeCssClass = getDensityModeCssClass(
            columnHeaderRoomy,
            columnHeaderCozy,
            columnHeaderCompact
        );
        const shouldShowColumnHeaderStyles =
            isFeatureEnabled('tri-mlFocusSelect') && isFeatureEnabled('tri-mlColumnHeaderHover');
        const { senderColumnWidth, subjectColumnWidth, receivedColumnWidth } =
            getMailListLayoutStore();
        const isRPDenselessEnabled = isFeatureEnabled('rp-denseless');
        /* eslint-disable-next-line owa-custom-rules/prefer-react-state-without-arrays-or-objects -- (https://aka.ms/OWALintWiki)
         * Please remove the array or object from React.useState() or leave a justification in case is not possible to do so.
         *	> It is preferable not to use arrays or objects as react state, destruct the state into primitive data types or use satchel instead. */
        const [currentSortColumn, setCurrentSortColumn] = useState<SortColumn | null>(null);
        const [currentSortDirection, setCurrentSortDirection] = useState<SortDirection | null>(
            /* eslint-disable-next-line owa-custom-rules/prefer-react-state-without-arrays-or-objects -- (https://aka.ms/OWALintWiki)
             * Please remove the array or object from React.useState() or leave a justification in case is not possible to do so.
             *	> It is preferable not to use arrays or objects as react state, destruct the state into primitive data types or use satchel instead. */
            null
        );

        const containerRef = React.useRef<HTMLDivElement>(null);

        /**
         * Using this tracking ref is a workaround as useResizeObserver isn't working
         * how I'd expect. Instead of only firing when the element ref's size changes,
         * it appears to be over-firing. I'm using this tracking ref to determine if
         * the callback needs to actually run.
         */
        const prevContainerWidth = React.useRef(null);

        React.useEffect(() => {
            let containerWidth: number | undefined;
            wrapForcedLayout('MailListColumnHeaders_containerWidth', () => {
                /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
                 * This is baseline exception, if you edit this file you need to fix this exception.
                 * Baseline, please provide a proper justification if touching this code
                 *	> 'offsetWidth' is restricted from being used. This property can cause performance problems by causing re-layouts. Please avoid if possible; if not, move to a requestAnimationFrame callback, and perform all DOM reads before performing any writes. */
                containerWidth = containerRef?.current?.offsetWidth;
            });
            if (!containerWidth) {
                return;
            }

            const columnWidths = calculateColumnWidths(
                true /* isInitialLayout */,
                containerWidth,
                prevContainerWidth
            );
            if (
                !columnWidths.senderColumnWidth ||
                !columnWidths.subjectColumnWidth ||
                !columnWidths.receivedColumnWidth
            ) {
                return;
            }
            onColumnHeaderWidthsUpdated(
                columnWidths.senderColumnWidth,
                columnWidths.subjectColumnWidth,
                columnWidths.receivedColumnWidth
            );
        }, []);

        // Resize columns if container size changes.
        useResizeObserver('MailListColumnHeaders', containerRef, rect => {
            const containerWidth = rect.width;
            // Don't update column widths if container isn't visible on screen.
            if (!containerWidth) {
                return;
            }

            const columnWidths = calculateColumnWidths(
                false /* isInitialLayout */,
                containerWidth,
                prevContainerWidth
            );
            if (
                !columnWidths.senderColumnWidth ||
                !columnWidths.subjectColumnWidth ||
                !columnWidths.receivedColumnWidth
            ) {
                return;
            }
            onColumnHeaderWidthsUpdated(
                columnWidths.senderColumnWidth,
                columnWidths.subjectColumnWidth,
                columnWidths.receivedColumnWidth
            );
        });

        const onColumnResize = React.useCallback(
            (
                firstElementWidth: number,
                secondElementWidth: number,
                headerType: MailListColumnHeaderType
            ) => {
                if (headerType === 0) {
                    onFirstColumnHandleChanged(firstElementWidth, secondElementWidth);
                } else if (headerType === 1) {
                    onSecondColumnHandleChanged(firstElementWidth, secondElementWidth);
                }
            },
            []
        );

        const isSortingDisabled = React.useMemo(() => {
            const tableView = getListViewStore().tableViews.get(tableViewId);
            if (!tableView) {
                // Disable sorting if tableView is undefined
                return true;
            }

            const categoryName = getUserCategoryName(tableView);
            const isCategoryFolder = categoryName !== null;
            const isDumpster = isDumpsterTable(tableView.tableQuery);
            const isGroupTable = tableView.tableQuery.type === 2 || tableView.tableQuery.type === 3;
            const isSearchTable = tableView.tableQuery.type === 1;
            const isPriorityViewValue = isPriorityView(
                tableView.tableQuery as MailFolderTableQuery
            );

            // Determine if current table is a new persona favorite.
            const isPersonaFavoriteFolder = getSelectedNode().type === 1;
            const isInitialPersonaFavoriteFolder =
                isPersonaFavoriteFolder && getViewFilterForTable(tableView) == null;

            return (
                isCategoryFolder ||
                isDumpster ||
                isGroupTable ||
                isSearchTable ||
                isCopilotResultsTable(tableView.tableQuery) ||
                isInitialPersonaFavoriteFolder ||
                isPriorityViewValue
            );
        }, [tableViewId]);

        // Reset sort column/direction when folder or folder sort changes.
        React.useEffect(() => {
            // If sorting is disabled, then we don't need to get sort info.
            if (isSortingDisabled) {
                return;
            }

            const tableView = getListViewStore().tableViews.get(tableViewId);
            if (!tableView) {
                return;
            }

            const sortBy = getSortByForTable(tableView);
            /* eslint-disable-next-line owa-custom-rules/forbid-react-set-state-and-mutators-inside-specific-hooks -- (https://aka.ms/OWALintWiki)
             * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error.
             *	> React setState function 'setCurrentSortColumn' cannot be called inside 'useEffect' to avoid unnecessary re-renders */
            setCurrentSortColumn(sortBy.sortColumn);
            /* eslint-disable-next-line owa-custom-rules/forbid-react-set-state-and-mutators-inside-specific-hooks -- (https://aka.ms/OWALintWiki)
             * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error.
             *	> React setState function 'setCurrentSortDirection' cannot be called inside 'useEffect' to avoid unnecessary re-renders */
            setCurrentSortDirection(sortBy.sortDirection);
        }, [folderId, isSortingDisabled, tableViewId]);

        const onColumnClicked = React.useCallback(
            (sortColumn: SortColumn) => {
                /**
                 * The default sort for the "Received" column is "Descending" (most
                 * recent on top) where as the default sort for the other columns is
                 * "Ascending" (albabetical order).
                 */
                let sortDirection: SortDirection = sortColumn === 1 ? 'Descending' : 'Ascending';

                /**
                 * If sort column is the same as current sort, then invert the sort
                 * direction. Otherwise, default to "Descending" sort.
                 */
                if (sortColumn === currentSortColumn) {
                    switch (currentSortDirection) {
                        case 'Descending':
                            sortDirection = 'Ascending';
                            break;
                        case 'Ascending':
                        default:
                            sortDirection = 'Descending';
                            break;
                    }
                }

                setCurrentSortColumn(sortColumn);
                setCurrentSortDirection(sortDirection);

                lazySelectSort.importAndExecute(
                    sortColumn,
                    sortDirection,
                    tableViewId,
                    'ListViewColumnHeader'
                );
            },
            [currentSortColumn, currentSortDirection, tableViewId]
        );

        const onSortByContextualMenu = React.useCallback(
            (sortColumn: SortColumn, sortDirection: SortDirection) => {
                setCurrentSortColumn(sortColumn);
                setCurrentSortDirection(sortDirection);
            },
            []
        );

        /**
         * Don't render column headers if sort info hasn't been initialized and sorting
         * is enabled. If sorting is disabled, then we don't need sort info and we
         * can render.
         */
        if (!currentSortColumn && !isSortingDisabled) {
            return null;
        }

        return (
            <div
                className={classnames(
                    isRPDenselessEnabled ? columnHeadersContainerDenseless : columnHeadersContainer,
                    !isSortingDisabled && sortingEnabled,
                    isRPDenselessEnabled && !isSortingDisabled && rpDenseless,
                    shouldShowColumnHeaderStyles && columnHeadersContainerFlexBox,
                    shouldShowColumnHeaderStyles && !isSortingDisabled && newFocusStyles,
                    shouldShowColumnHeaderStyles && mlColumnHeaderHover,
                    densityModeCssClass
                )}
                ref={containerRef}
            >
                {!shouldShowColumnHeaderStyles && (
                    <div className={classnames(emptyFirstColumn, densityModeCssClass)} />
                )}
                <MailListColumnHeader
                    currentSortColumn={currentSortColumn}
                    currentSortDirection={currentSortDirection}
                    folderId={folderId}
                    headerType={0}
                    isSortingDisabled={isSortingDisabled}
                    onColumnClicked={onColumnClicked}
                    onColumnResize={onColumnResize}
                    onSortByContextualMenu={onSortByContextualMenu}
                    tableViewId={tableViewId}
                    width={senderColumnWidth ?? 0}
                />
                <MailListColumnHeader
                    currentSortColumn={currentSortColumn}
                    currentSortDirection={currentSortDirection}
                    folderId={folderId}
                    headerType={1}
                    isSortingDisabled={isSortingDisabled}
                    onColumnClicked={onColumnClicked}
                    onColumnResize={onColumnResize}
                    onSortByContextualMenu={onSortByContextualMenu}
                    tableViewId={tableViewId}
                    width={subjectColumnWidth ?? 0}
                />
                <MailListColumnHeader
                    currentSortColumn={currentSortColumn}
                    currentSortDirection={currentSortDirection}
                    folderId={folderId}
                    headerType={2}
                    isSortingDisabled={isSortingDisabled}
                    onColumnClicked={onColumnClicked}
                    onColumnResize={onColumnResize}
                    onSortByContextualMenu={onSortByContextualMenu}
                    tableViewId={tableViewId}
                    width={receivedColumnWidth ?? 0}
                />
            </div>
        );
    },
    'MailListColumnHeaders'
);

export interface MailListColumnHeaderProps {
    currentSortColumn: SortColumn | null;
    currentSortDirection: SortDirection | null;
    folderId: string;
    headerType: MailListColumnHeaderType;
    isSortingDisabled: boolean;
    onColumnClicked: (sortColumn: SortColumn) => void;
    onColumnResize: (
        firstElementWidth: number,
        secondElementWidth: number,
        headerType: MailListColumnHeaderType
    ) => void;
    onSortByContextualMenu: (sortColumn: SortColumn, sortDirection: SortDirection) => void;
    tableViewId: string;
    width: number;
}
const MailListColumnHeader = observer(function MailListColumnHeader(
    props: MailListColumnHeaderProps
) {
    const {
        currentSortColumn,
        currentSortDirection,
        folderId,
        headerType,
        isSortingDisabled,
        onColumnClicked,
        onColumnResize,
        onSortByContextualMenu,
        tableViewId,
        width,
    } = props;

    // Context menu state
    const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
    /* eslint-disable-next-line owa-custom-rules/prefer-react-state-without-arrays-or-objects -- (https://aka.ms/OWALintWiki)
     * Please remove the array or object from React.useState() or leave a justification in case is not possible to do so.
     *	> It is preferable not to use arrays or objects as react state, destruct the state into primitive data types or use satchel instead. */
    const [anchorPoint, setAnchorPoint] = useState<IPoint | null>(null);

    const shouldShowColumnHeaderStyles =
        isFeatureEnabled('tri-mlFocusSelect') && isFeatureEnabled('tri-mlColumnHeaderHover');
    const isRPDenselessEnabled = isFeatureEnabled('rp-denseless');

    // For received column, don't set explicit width - it will flex to fill remaining space
    const isReceivedColumn = headerType === 2;
    const buttonStyles = useComputedValue(() => {
        let computedWidth: number | undefined = width;
        if (shouldShowColumnHeaderStyles && isReceivedColumn) {
            computedWidth = undefined;
        }

        return {
            width: computedWidth,
            boxSizing: shouldShowColumnHeaderStyles ? ('border-box' as const) : undefined,
            cursor: isSortingDisabled ? 'default' : 'pointer',
        };
    }, [isSortingDisabled, isReceivedColumn, width]);

    const onResized = React.useCallback(
        (firstElementWidth: number, secondElementWidth: number) => {
            onColumnResize(firstElementWidth, secondElementWidth, headerType);
        },
        [onColumnResize, headerType]
    );

    const folderName = folderId ? folderIdToName(folderId, true /* shouldSearchShared */) : null;

    const onClick = React.useCallback(
        (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            evt.stopPropagation();

            onColumnClicked(getSortColumnByMailListColumnHeaderType(headerType, folderName ?? ''));
        },
        [headerType, folderName, onColumnClicked]
    );

    const onContextMenu = React.useCallback(
        (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            evt.preventDefault();
            evt.stopPropagation();
            setAnchorPoint(getAnchorForContextMenu(evt));
            setIsContextMenuVisible(true);
        },
        []
    );

    const onDismiss = React.useCallback(
        (evt?: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) => {
            if (evt) {
                evt.stopPropagation();
            }

            setIsContextMenuVisible(false);
        },
        []
    );

    const iconClass = getDensityModeCssClass(
        sortIconRoomy,
        sortIconCompactOrCozy,
        sortIconCompactOrCozy
    );

    const icon = React.useMemo(() => {
        // Get sort column associated with header type.
        const sortColumnForHeader = getSortColumnByMailListColumnHeaderType(
            headerType,
            folderName ?? ''
        );

        /**
         * Don't render icon if table isn't sorted by this column or if sorting
         * is disabled.
         */
        if (currentSortColumn !== sortColumnForHeader || isSortingDisabled) {
            return null;
        }

        // Return icon based on sort direction.
        const isSortDirectionAscending = currentSortDirection === 'Ascending';
        return isSortDirectionAscending ? (
            <ChevronUpRegular className={iconClass} />
        ) : (
            <ChevronDownRegular className={iconClass} />
        );
    }, [currentSortColumn, currentSortDirection, isSortingDisabled, tableViewId, iconClass]);

    const columnLabel = getColumnLabel(folderId, headerType);

    const resizeHandleScenario = React.useMemo(() => {
        // Sender column always refers to the first resize handle, so return
        // scenario 1.
        if (headerType === 0) {
            return ResizeHandleScenario.MailListColumnHeader1;
        }

        if (headerType === 1) {
            return ResizeHandleScenario.MailListColumnHeader2;
        }

        if (headerType === 2) {
            return ResizeHandleScenario.MailListColumnHeader2;
        }

        return null;
    }, [headerType]);

    const resizeHandleComponent = React.useMemo(() => {
        // There's no resize handle after the last column.
        if (headerType === 2 || resizeHandleScenario === null) {
            return null;
        }

        // Otherwise, we return an actual ResizeHandle component that can be resized.
        return (
            <ResizeHandle
                onResized={onResized}
                className={classnames(
                    shouldShowColumnHeaderStyles ? resizeHandle : resizeHandleDeprecated
                )}
                scenario={resizeHandleScenario}
            />
        );
    }, [headerType, shouldShowColumnHeaderStyles, onResized, resizeHandleScenario]);

    return (
        <>
            <button
                aria-label={format(loc(columnHeaderAriaLabel), columnLabel)}
                type="button"
                className={classnames(
                    columnHeader,
                    useMessageListTextStyle('Major'),
                    !shouldShowColumnHeaderStyles && !isSortingDisabled && columnHeaderHovered,
                    isRPDenselessEnabled && bleedthroughColumnHeader,
                    shouldShowColumnHeaderStyles && headerType === 0 && senderColumnButtonMargin,
                    columnHeaderContainer,
                    shouldShowColumnHeaderStyles && headerType === 2 && receivedColumnContainer
                )}
                data-min-width={getColumnMinWidth(headerType)}
                disabled={isSortingDisabled}
                onClick={onClick}
                onContextMenu={onContextMenu}
                style={buttonStyles}
                title={columnLabel}
            >
                {columnLabel}
                {icon}
            </button>

            {resizeHandleComponent}

            {isContextMenuVisible && (
                <FluentMenu
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    items={getMenuItems(
                        getSortColumnByMailListColumnHeaderType(headerType, folderName ?? ''),
                        onSortByContextualMenu,
                        tableViewId,
                        resizeHandleScenario
                    )}
                    onDismiss={onDismiss}
                    target={anchorPoint}
                />
            )}
        </>
    );
},
'MailListColumnHeader');

const getSortColumnByMailListColumnHeaderType = (
    mailListColumnHeaderType: MailListColumnHeaderType,
    folderName: string
): SortColumn => {
    switch (mailListColumnHeaderType) {
        case 0:
            return folderName == 'sentitems' || folderName == 'drafts' ? 9 : 3;
        case 1:
            return 8;
        case 2:
            return folderName == 'scheduled' ? 15 : 1;
    }
};

const getMenuItems = (
    sortColumn: SortColumn,
    onSortByContextualMenu: (sortColumn: SortColumn, sortDirection: SortDirection) => void,
    tableViewId: string,
    resizeHandleScenario: ResizeHandleScenario | null
): IContextualMenuItem[] => {
    const menuItems: IContextualMenuItem[] = [];

    if (!getIsSearchTableShown()) {
        menuItems.push(
            {
                key: 'Ascending',
                name: loc(sortAscendingLabel),
                onClick: () => {
                    onSortByContextualMenu(sortColumn, 'Ascending');
                    lazySelectSort.importAndExecute(
                        sortColumn,
                        'Ascending',
                        tableViewId,
                        'ListViewColumnHeaderContextMenu'
                    );
                },
            },
            {
                key: 'Descending',
                name: loc(sortDescendingLabel),
                onClick: () => {
                    onSortByContextualMenu(sortColumn, 'Descending');
                    lazySelectSort.importAndExecute(
                        sortColumn,
                        'Descending',
                        tableViewId,
                        'ListViewColumnHeaderContextMenu'
                    );
                },
            },
            getDisplayDensityMenuItem()
        );
    }

    const isColumnResizable = resizeHandleScenario !== null;
    if (isColumnResizable) {
        menuItems.push({
            key: 'Resize',
            name: loc(resizeButtonLabel),
            onClick: () => {
                lazyUpdateScenarioInResizeMode.importAndExecute(resizeHandleScenario);
            },
        });
    }

    return menuItems;
};

const getColumnLabel = (folderId: string, headerType: MailListColumnHeaderType): string => {
    const folderName = folderId ? folderIdToName(folderId, true) : null;

    switch (headerType) {
        case 0:
            return folderName == 'sentitems' || folderName == 'drafts' || folderName == 'outbox'
                ? loc(toColumnLabel)
                : loc(fromColumnLabel);
        case 1:
            return loc(subjectColumnLabel);
        case 2: {
            if (folderName == 'sentitems') {
                return loc(sentColumnLabel);
            } else if (folderName == 'drafts') {
                return loc(modifiedColumnLabel);
            } else if (folderName == 'scheduled') {
                return loc(returnTimeColumnLabel);
            } else {
                return loc(receivedColumnLabel);
            }
        }
        default:
            return '';
    }
};

export default MailListColumnHeaders;
